/* eslint-disable react/jsx-props-no-spreading */
import {
  Button,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useToken,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { UserIssueType, UserSentimentType } from '@/client/services/api/graphql/gql/graphql';

import { ChevronDownIcon } from '@chakra-ui/icons';
import { hexToRGBA } from '@/client/utils/hexToRGBA';
import { useAddExchangeFeedback } from './hooks/useAddExchangeFeedback';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// TODO: Submit issue type once added to graph

interface AIAgentFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  exchangeId: string;
}

export default function AIAgentFeedbackModal({
  isOpen,
  onClose,
  exchangeId,
}: AIAgentFeedbackModalProps) {
  const { t } = useTranslation();
  const [baseBlack] = useToken('colors', ['baseBlack']);
  const { addExchangeFeedback } = useAddExchangeFeedback();

  const issueTypeOptions = [
    {
      label: t('aiAgent.feedback.issueType.inaccurateOrLackingInformation'),
      value: UserIssueType.InaccurateInformation,
    },
    {
      label: t('aiAgent.feedback.issueType.outdatedInformation'),
      value: UserIssueType.OutdatedInformation,
    },
    {
      label: t('aiAgent.feedback.issueType.eddyMisunderstoodMyRequest'),
      value: UserIssueType.AgentMisunderstood,
    },
    {
      label: t('aiAgent.feedback.issueType.technicalBug'),
      value: UserIssueType.TechnicalBug,
    },
    {
      label: t('aiAgent.feedback.issueType.other'),
      value: UserIssueType.Other,
    },
  ];

  const methods = useForm({
    defaultValues: {
      issueType: '',
      feedback: '',
    },
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        issueType: z.nativeEnum(UserIssueType, {
          required_error: `${t('aiAgent.feedback.issueType.required')}`,
        }),
        feedback: z.string().optional(),
      }),
    ),
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    register,
    formState: { isValid },
  } = methods;

  const { issueType } = watch();

  const onSubmit = handleSubmit((data) => {
    addExchangeFeedback
      .mutateAsync({
        feedbackInput: {
          issueType: data.issueType as UserIssueType,
          userFeedback: data.feedback,
          userSentiment: UserSentimentType.Negative,
        },
        id: exchangeId,
      })
      .then(() => {
        reset();
        onClose();
      });
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <ModalContent paddingX={8} paddingY={6}>
            <ModalBody paddingX={0} paddingBottom={6}>
              <Stack spacing={6}>
                <Text fontSize="24px" fontWeight={600} color="baseBlack">
                  {t('aiAgent.feedbackModal.title')}
                </Text>

                <Stack spacing={2}>
                  <Text fontSize="14px" fontWeight={400} color={hexToRGBA(baseBlack, 0.6)}>
                    {t('aiAgent.feedbackModal.helpText')}
                  </Text>

                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon boxSize={6} color="baseBlack" />}
                      backgroundColor="white"
                      color="neutral.600"
                      border="1px solid"
                      borderColor="warmNeutral.200"
                      borderRadius="6px"
                      fontSize="14px"
                      fontWeight={500}
                      textAlign="left"
                      _hover={{ backgroundColor: 'white' }}
                      _active={{ backgroundColor: 'white' }}
                    >
                      {issueType
                        ? issueTypeOptions.find((option) => option.value === issueType)?.label
                        : t('aiAgent.feedback.issueType.placeholder')}
                    </MenuButton>

                    <MenuList
                      border="1px solid"
                      borderColor="warmNeutral.200"
                      borderRadius="6px"
                      paddingX={4}
                    >
                      {issueTypeOptions.map((option, index) => (
                        <MenuItem
                          key={option.value}
                          fontSize="14px"
                          fontWeight={500}
                          borderBottom={
                            index !== issueTypeOptions.length - 1 ? '1px solid' : 'none'
                          }
                          borderBottomColor="warmNeutral.200"
                          paddingY={4}
                          onClick={() =>
                            setValue('issueType', option.value, { shouldValidate: true })
                          }
                          _hover={{ backgroundColor: 'warmNeutral.0' }}
                          _active={{ backgroundColor: 'warmNeutral.0' }}
                          _focus={{ backgroundColor: 'warmNeutral.0' }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </Stack>

                <Stack spacing={2}>
                  <Text fontSize="14px" fontWeight={400} color={hexToRGBA(baseBlack, 0.6)}>
                    {t('aiAgent.feedbackModal.details')}
                  </Text>

                  <Textarea
                    {...register('feedback', { required: false })}
                    resize="none"
                    placeholder="Add a description..."
                    border="1px solid"
                    borderColor="warmNeutral.200"
                    borderRadius="6px"
                    fontSize="14px"
                    _placeholder={{ color: 'neutral.500', fontSize: '14px', fontWeight: 500 }}
                    _focusVisible={{ borderColor: 'warmNeutral.200' }}
                  />
                </Stack>
              </Stack>
            </ModalBody>

            <ModalFooter padding={0}>
              <ButtonGroup gap={4}>
                <Button
                  variant="cancel"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  {t('aiAgent.feedbackModal.cancel')}
                </Button>

                <Button type="submit" variant="AIAgent" isDisabled={!isValid}>
                  {t('aiAgent.feedbackModal.submit')}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
}
